<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type {
  DraftRacePriceTier,
  RacePriceTier,
  RacePriceTierStatus,
  RacePricing,
} from '@/types/events'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import RacePriceTierSlideOver from './RacePriceTierSlideOver.vue'
import { useRacePriceTiers } from '@/composables/useRacePriceTiers'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'
import { formatWallClock, priceToCents } from '@/utils/racePriceTier'

const MAX_TIERS = 10
const DEFAULT_CURRENCY = 'eur'
const TIER_FIELDS = ['name', 'price', 'ends_at', 'max_registrations'] as const

interface DisplayRow {
  key: string | number
  index: number
  name: string
  price: string
  ends_at: string | null
  max_registrations: number | null
  registrations_count: number | null
  remaining_spots: number | null
  status: RacePriceTierStatus | null
  has_registrations: boolean
  hasErrors: boolean
}

const props = defineProps<{
  mode: 'draft' | 'persisted'
  organisationId: number
  timezone: string
  /** Value of the standard (base) price field, shown as the fixed last row. */
  standardPrice: string
  eventSlug?: string
  editionSlug?: string
  raceSlug?: string
  initialTiers?: RacePriceTier[]
  initialPricing?: RacePricing
  modelValue?: DraftRacePriceTier[]
  /** Raw 422 `errors` object of the race create response. */
  draftErrors?: Record<string, string[]>
}>()

const emit = defineEmits<{
  changed: []
  'update:modelValue': [tiers: DraftRacePriceTier[]]
}>()

const { t, locale } = useI18n()

const isDraft = computed<boolean>(() => props.mode === 'draft')

const persisted = useRacePriceTiers(props.eventSlug ?? '', props.editionSlug ?? '', props.raceSlug ?? '')

const panelOpen = ref(false)
const editingTier = ref<RacePriceTier | DraftRacePriceTier | null>(null)
const editingIndex = ref<number | null>(null)
const confirmDeleteKey = ref<string | number | null>(null)

// Draft errors are local so reordering/removing rows can clear them (indices no longer match).
const localDraftErrors = ref<Record<string, string[]>>({})
watch(
  () => props.draftErrors,
  (errors) => {
    localDraftErrors.value = { ...(errors ?? {}) }
  },
  { immediate: true },
)

onMounted(() => {
  if (isDraft.value) return
  if (props.initialTiers !== undefined) {
    persisted.setInitial(props.initialTiers, props.initialPricing ?? null)
  } else {
    void persisted.fetchTiers()
  }
})

function draftRowHasErrors(index: number): boolean {
  return Object.keys(localDraftErrors.value).some((key) => key.startsWith(`price_tiers.${index}.`))
}

function draftRowErrors(index: number): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const field of TIER_FIELDS) {
    const messages = localDraftErrors.value[`price_tiers.${index}.${field}`]
    if (messages !== undefined) result[field] = messages
  }
  return result
}

const rows = computed<DisplayRow[]>(() => {
  if (isDraft.value) {
    return (props.modelValue ?? []).map((tier, index) => ({
      key: tier.key,
      index,
      name: tier.name,
      price: tier.price,
      ends_at: tier.ends_at,
      max_registrations: tier.max_registrations,
      registrations_count: null,
      remaining_spots: null,
      status: null,
      has_registrations: false,
      hasErrors: draftRowHasErrors(index),
    }))
  }
  return persisted.tiers.value.map((tier, index) => ({
    key: tier.id,
    index,
    name: tier.name,
    price: tier.price,
    ends_at: tier.ends_at,
    max_registrations: tier.max_registrations,
    registrations_count: tier.registrations_count,
    remaining_spots: tier.remaining_spots,
    status: tier.status,
    has_registrations: tier.has_registrations,
    hasErrors: false,
  }))
})

const atLimit = computed<boolean>(() => rows.value.length >= MAX_TIERS)

const sectionError = computed<string | null>(() => {
  if (isDraft.value) return localDraftErrors.value['price_tiers']?.[0] ?? null
  return persisted.error.value
})

const busy = computed<boolean>(() => persisted.loading.value || persisted.actionLoading.value)

const pricing = computed<RacePricing | null>(() => (isDraft.value ? null : persisted.pricing.value))

const currentPriceText = computed<string | null>(() => {
  if (pricing.value === null) return null
  const price = formatCentsAsCurrency(pricing.value.current_price_cents, pricing.value.currency, locale.value)
  const tier = pricing.value.current_tier
  return tier === null
    ? t('races.price_tiers.current_price', { price })
    : t('races.price_tiers.current_price_tier', { price, tier: tier.name })
})

const currentSpotsText = computed<string | null>(() => {
  const spots = pricing.value?.current_tier?.remaining_spots
  if (spots === null || spots === undefined) return null
  return t('races.price_tiers.summary_spots_left', { count: spots }, spots)
})

const standardPriceText = computed<string>(() => {
  const trimmed = props.standardPrice.trim()
  if (trimmed === '' || Number.isNaN(Number(trimmed))) return '-'
  return formatCentsAsCurrency(priceToCents(trimmed), DEFAULT_CURRENCY, locale.value)
})

const confirmBody = computed<string>(() =>
  isDraft.value ? t('races.price_tiers.delete_confirm_draft') : t('races.price_tiers.delete_confirm'),
)

function priceText(price: string): string {
  return formatCentsAsCurrency(priceToCents(price), DEFAULT_CURRENCY, locale.value)
}

function boundText(row: DisplayRow): string {
  const until = row.ends_at !== null ? t('races.price_tiers.bound_until', { date: formatWallClock(row.ends_at, locale.value) }) : null
  let cap: string | null = null
  if (row.max_registrations !== null) {
    cap =
      row.registrations_count === null
        ? t('races.price_tiers.bound_cap_draft', { max: row.max_registrations })
        : t('races.price_tiers.bound_cap', { count: row.registrations_count, max: row.max_registrations })
  }
  if (until !== null && cap !== null) {
    return t('races.price_tiers.bound_or', { first: until, second: cap })
  }
  return until ?? cap ?? ''
}

function statusClass(status: RacePriceTierStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/15 text-emerald-300'
    case 'upcoming':
      return 'bg-sky-500/15 text-sky-300'
    case 'sold_out':
      return 'bg-amber-500/15 text-amber-300'
    default:
      return 'bg-slate-600/40 text-slate-300'
  }
}

function openCreate(): void {
  if (atLimit.value) return
  editingTier.value = null
  editingIndex.value = null
  panelOpen.value = true
}

function openEdit(row: DisplayRow): void {
  editingIndex.value = row.index
  editingTier.value = isDraft.value
    ? ((props.modelValue ?? [])[row.index] ?? null)
    : (persisted.tiers.value.find((tier) => tier.id === row.key) ?? null)
  panelOpen.value = true
}

function closePanel(): void {
  panelOpen.value = false
}

const editingExternalErrors = computed<Record<string, string[]> | undefined>(() =>
  isDraft.value && editingIndex.value !== null ? draftRowErrors(editingIndex.value) : undefined,
)

async function handleSaved(saved: RacePriceTier | DraftRacePriceTier): Promise<void> {
  if (isDraft.value) {
    const draft = saved as DraftRacePriceTier
    const list = [...(props.modelValue ?? [])]
    if (editingIndex.value !== null) {
      list[editingIndex.value] = draft
      clearDraftRowErrors(editingIndex.value)
    } else {
      list.push(draft)
    }
    emit('update:modelValue', list)
    panelOpen.value = false
    return
  }
  panelOpen.value = false
  await persisted.fetchTiers()
  emit('changed')
}

function clearDraftRowErrors(index: number): void {
  const next = { ...localDraftErrors.value }
  for (const key of Object.keys(next)) {
    if (key.startsWith(`price_tiers.${index}.`)) delete next[key]
  }
  localDraftErrors.value = next
}

async function move(row: DisplayRow, direction: 'up' | 'down'): Promise<void> {
  if (isDraft.value) {
    const list = [...(props.modelValue ?? [])]
    const target = direction === 'up' ? row.index - 1 : row.index + 1
    const current = list[row.index]
    const other = list[target]
    if (current === undefined || other === undefined) return
    list[row.index] = other
    list[target] = current
    localDraftErrors.value = {}
    emit('update:modelValue', list)
    return
  }
  const ok = await persisted.moveTier(row.key as number, direction)
  if (ok) emit('changed')
}

function requestDelete(row: DisplayRow): void {
  if (!isDraft.value && row.has_registrations) return
  confirmDeleteKey.value = row.key
}

function cancelDelete(): void {
  confirmDeleteKey.value = null
}

async function confirmDelete(): Promise<void> {
  const key = confirmDeleteKey.value
  if (key === null) return
  if (isDraft.value) {
    emit(
      'update:modelValue',
      (props.modelValue ?? []).filter((tier) => tier.key !== key),
    )
    localDraftErrors.value = {}
    confirmDeleteKey.value = null
    return
  }
  const ok = await persisted.deleteTier(key as number)
  confirmDeleteKey.value = null
  if (ok) emit('changed')
}
</script>

<template>
  <div class="mt-8 border-t border-slate-700 pt-6" data-testid="price-tiers-section">
    <h3 class="text-sm font-semibold text-white">{{ t('races.price_tiers.title') }}</h3>
    <p class="mt-1 text-xs text-slate-400">{{ t('races.price_tiers.description') }}</p>
    <p v-if="isDraft" class="mt-1 text-xs text-slate-500" data-testid="price-tiers-draft-note">
      {{ t('races.price_tiers.draft_note') }}
    </p>

    <p
      v-if="currentPriceText"
      class="mt-3 text-sm text-slate-200"
      data-testid="price-tiers-current-price"
    >
      {{ currentPriceText }}
      <span v-if="currentSpotsText" class="text-slate-400"> - {{ currentSpotsText }}</span>
    </p>

    <div
      v-if="sectionError"
      class="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      role="alert"
      data-testid="price-tiers-error"
    >
      {{ sectionError }}
    </div>

    <div v-if="!isDraft && persisted.loading.value && rows.length === 0" class="mt-4 flex items-center gap-2 text-sm text-slate-400">
      <AppSpinner size="button" />
      {{ t('races.price_tiers.loading') }}
    </div>

    <p
      v-else-if="rows.length === 0"
      class="mt-4 text-sm text-slate-400"
      data-testid="price-tiers-empty"
    >
      {{ t('races.price_tiers.empty') }}
    </p>

    <ul v-if="rows.length > 0" class="mt-4 space-y-2" data-testid="price-tiers-list">
      <li
        v-for="row in rows"
        :key="row.key"
        class="rounded-lg border bg-slate-900/40 px-4 py-3"
        :class="row.hasErrors ? 'border-red-500' : 'border-slate-700'"
        data-testid="price-tier-row"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="flex flex-wrap items-center gap-2 text-sm font-medium text-white">
              <span class="truncate">{{ row.name }}</span>
              <span class="text-slate-300">{{ priceText(row.price) }}</span>
              <span
                v-if="row.status"
                class="rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusClass(row.status)"
                data-testid="price-tier-status"
              >
                {{ t(`races.price_tiers.status.${row.status}`) }}
              </span>
            </p>
            <p class="mt-0.5 text-xs text-slate-400">
              {{ boundText(row) }}
              <template v-if="row.max_registrations !== null && row.remaining_spots !== null">
                - {{ t('races.price_tiers.remaining', { count: row.remaining_spots }, row.remaining_spots) }}
              </template>
            </p>
            <p v-if="row.hasErrors" class="mt-1 text-xs text-red-400" data-testid="price-tier-row-errors">
              {{ t('races.price_tiers.row_has_errors') }}
            </p>
          </div>

          <div class="flex items-center gap-1">
            <button
              type="button"
              :disabled="row.index === 0 || busy"
              class="rounded-lg px-2 py-1 text-xs text-slate-300 hover:bg-slate-700/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              :aria-label="t('races.price_tiers.move_up')"
              :title="t('races.price_tiers.move_up')"
              data-testid="price-tier-move-up"
              @click="move(row, 'up')"
            >
              &uarr;
            </button>
            <button
              type="button"
              :disabled="row.index === rows.length - 1 || busy"
              class="rounded-lg px-2 py-1 text-xs text-slate-300 hover:bg-slate-700/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              :aria-label="t('races.price_tiers.move_down')"
              :title="t('races.price_tiers.move_down')"
              data-testid="price-tier-move-down"
              @click="move(row, 'down')"
            >
              &darr;
            </button>
            <button
              type="button"
              :disabled="busy"
              class="rounded-lg px-2 py-1 text-xs text-slate-300 hover:bg-slate-700/50 hover:text-white disabled:opacity-40"
              data-testid="price-tier-edit"
              @click="openEdit(row)"
            >
              {{ t('races.price_tiers.edit_action') }}
            </button>
            <button
              type="button"
              :disabled="busy || (!isDraft && row.has_registrations)"
              class="rounded-lg px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
              :title="!isDraft && row.has_registrations ? t('races.price_tiers.delete_locked') : undefined"
              data-testid="price-tier-delete"
              @click="requestDelete(row)"
            >
              {{ t('races.price_tiers.delete') }}
            </button>
          </div>
        </div>
      </li>
    </ul>

    <!-- Fixed final row: the standard price, edited in the base price field -->
    <div
      class="mt-2 flex items-center justify-between rounded-lg border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300"
      data-testid="price-tier-standard-row"
    >
      <span>{{ t('races.price_tiers.standard_row') }}</span>
      <span class="font-medium text-white">{{ standardPriceText }}</span>
    </div>

    <div class="mt-4">
      <button
        type="button"
        :disabled="atLimit || busy"
        class="inline-flex items-center rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="price-tier-add"
        @click="openCreate"
      >
        {{ t('races.price_tiers.add') }}
      </button>
      <p v-if="atLimit" class="mt-1 text-xs text-slate-500" data-testid="price-tiers-limit">
        {{ t('races.price_tiers.limit_reached') }}
      </p>
    </div>

    <RacePriceTierSlideOver
      :open="panelOpen"
      :mode="mode"
      :tier="editingTier"
      :organisation-id="organisationId"
      :timezone="timezone"
      :event-slug="eventSlug"
      :edition-slug="editionSlug"
      :race-slug="raceSlug"
      :external-errors="editingExternalErrors"
      @close="closePanel"
      @saved="handleSaved"
    />

    <ConfirmDialog
      :open="confirmDeleteKey !== null"
      :title="t('races.price_tiers.delete_title')"
      :body="confirmBody"
      :confirm-label="t('races.price_tiers.delete')"
      danger
      :loading="persisted.actionLoading.value"
      @update:open="cancelDelete"
      @confirm="confirmDelete"
    />
  </div>
</template>
